import { AbstractControl, ValidationErrors } from '@angular/forms';
import { compact } from 'lodash-es';
import { RktFormFieldConfig, formCheckboxGroup, formRadioGroup } from '@rocketfinancialcorp/rocket-ui/form';

import { LinxAccountType, LinxEntityType } from '@shared/constants';
import { CustomerVerificationStatus } from '@shared/models';

const getPersonalInfoDetails = (accountType?: LinxAccountType, entityType?: LinxEntityType) => {
  const options = [
    { value: 'firstName', label: 'First name' },
    { value: 'lastName', label: 'Last name' },
    { value: 'middleName', label: 'Middle name' },
    { value: 'email', label: 'Email' },
  ];
  if (accountType == LinxAccountType.BUSINESS && entityType === LinxEntityType.COUNTERPARTY) {
    return [...options, { value: 'businessName', label: 'Business Name on Account' }];
  }
  return options;
};

export const linxRequestProfileFields = (
  accountType?: LinxAccountType,
  entityType?: LinxEntityType,
  verificationStatus?: CustomerVerificationStatus,
): RktFormFieldConfig[] => {
  // Only an actually-verified customer locks the KYC identity fields. An absent status (e.g. the template
  // builder, which has no customer) must read as editable, not verified - otherwise a blank new template
  // greys out firstName/lastName/middleName/socialId/dateOfBirth for no reason.
  const isCustomerVerified = verificationStatus != null && verificationStatus !== 'UNVERIFIED';
  return [
    {
      fieldGroup: [
        formCheckboxGroup({
          key: 'personalInfo',
          label: 'Personal Information',
          className: 'linx-checkbox-enhance-fields',
          props: {
            options: getPersonalInfoDetails(accountType, entityType),
          },
          expressions: {
            hide: () => entityType === LinxEntityType.CUSTOMER,
          },
        }),
        formCheckboxGroup({
          key: 'personalInfo',
          label: 'Personal Information',
          className: 'linx-checkbox-enhance-fields',
          props: {
            options: [
              { value: 'firstName', label: 'First name', disabled: isCustomerVerified },
              { value: 'lastName', label: 'Last name', disabled: isCustomerVerified },
              { value: 'middleName', label: 'Middle name', disabled: isCustomerVerified },
              { value: 'email', label: 'Email' },
              { value: 'salutation', label: 'Salutation' },
              { value: 'sex', label: 'Sex' },
              { value: 'socialId', label: 'Social ID', disabled: isCustomerVerified },
              { value: 'socialIdCountryCode', label: 'Social ID Country' },
              { value: 'dateOfBirth', label: 'Date of Birth', disabled: isCustomerVerified },
              { value: 'cityOfBirth', label: 'City of Birth' },
              { value: 'countryOfBirthCode', label: 'Country of Birth' },
              { value: 'citizenshipCountryCode', label: 'Citizenship Country' },
            ],
          },
          expressions: {
            hide: () => accountType !== 'INDIVIDUAL' || entityType !== LinxEntityType.CUSTOMER,
          },
        }),
        formCheckboxGroup({
          key: 'businessInfo',
          label: 'Business Information',
          className: 'linx-checkbox-enhance-fields',
          props: {
            options: [
              { value: 'businessName', label: 'Business Name on Account' },
              { value: 'contacts', label: 'Contact Person' },
              { value: 'doingBusinessAsName', label: 'Business DBA Name', disabled: isCustomerVerified },
              { value: 'legalName', label: 'Business Legal Name', disabled: isCustomerVerified },
              { value: 'legalEntityType', label: 'Legal Entity type' },
              { value: 'dateOfFormation', label: 'Date of Formation' },
              { value: 'taxId', label: 'Business Tax ID', disabled: isCustomerVerified },
              { value: 'taxIdCountryCode', label: 'Business Tax ID Country' },
              { value: 'industryCode', label: 'Industry' },
              { value: 'registeredCountryCode', label: 'Registered Country' },
              { value: 'operationCountryCodes', label: 'Countries of Operation' },
              // No `disabled: isCustomerVerified` — unlike the KYB identity fields, KOR leaves `website` editable
              // after verification (it is not in the backend's VERIFICATION_RESTRICTED_FIELDS).
              { value: 'website', label: 'Company Website' },
              { value: 'phoneNumber', label: 'Primary Phone of Business' },
            ],
          },
          expressions: {
            hide: () => accountType !== 'BUSINESS' || entityType !== LinxEntityType.CUSTOMER,
          },
        }),
        formRadioGroup({
          key: 'phoneNumber',
          className: 'linx-checkbox-enhance-fields',
          label: 'Phone Number Options',
          props: {
            options: [
              { value: 'MOBILE', label: 'Mobile' },
              { value: 'WORK', label: 'Work' },
              { value: 'HOME', label: 'Home' },
            ],
          },
          expressions: {
            hide: () => accountType !== 'INDIVIDUAL',
          },
        }),
        formCheckboxGroup({
          key: 'addresses',
          label: 'Address Options',
          className: 'linx-checkbox-enhance-fields',
          props: {
            options: [
              { value: 'PHYSICAL', label: 'Physical' },
              { value: 'MAILING', label: 'Mailing' },
              { value: 'SHIPPING', label: 'Shipping' },
            ],
          },
          expressions: {
            hide: () => entityType !== LinxEntityType.CUSTOMER,
          },
        }),
      ],
      className: 'linx-checkbox-group',
      validators: {
        required: (control: AbstractControl): ValidationErrors | null => {
          const controlArray = compact(Object.keys(control.value || {}).map((key) => control.value[key]));
          const nonEmpty: string[] = [];
          controlArray.forEach((item) => {
            const selectedOptions = Object.keys(item).filter((el) => item[el]);
            nonEmpty.push(...selectedOptions);
          });
          return nonEmpty.length ? { required: { message: 'error' } } : null;
        },
      },
    },
  ];
};

export const idDocumentIndividualFields = [
  formCheckboxGroup({
    key: 'documentTypes',
    label: '',
    className: 'linx-checkbox-enhance-fields',
    props: {
      options: [
        { value: 'driving-license', label: 'Drivers License' },
        { value: 'passport', label: 'Passport' },
        { value: 'other', label: 'Other' },
      ],
    },
  }),
];

export const idDocumentBusinessFields = [
  formCheckboxGroup({
    key: 'documentTypes',
    label: '',
    className: 'linx-checkbox-enhance-fields',
    props: {
      options: [
        { value: 'formation', label: 'Formation' },
        { value: 'tax', label: 'Tax' },
        { value: 'ownership', label: 'Ownership' },
        { value: 'other', label: 'Other' },
      ],
    },
  }),
];
